import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from '../utils/auth.utils';
import { NavigationService } from './navigation.service';
import { AmplitudeService } from './amplitude.service';
import { ISignInResponse } from '../interfaces/response/sign-in.response';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  AUTH_API_URL = `${environment.API_URL}/${environment.VERSION}/auth`;
  ACCOUNT_API_URL = `${environment.API_URL}/${environment.VERSION}/account`;

  private _http = inject(HttpClient);
  private _navigationService = inject(NavigationService);
  private _amplitudeService = inject(AmplitudeService);
  public IsLoggedIn: WritableSignal<boolean> = signal(false);

  constructor() {
    this.IsLoggedIn.set(!!this.accessToken);
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set refreshToken(refreshToken: string) {
    localStorage.setItem('refreshToken', refreshToken);
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  set userType(userType: string) {
    localStorage.setItem('userType', userType);
  }

  get userType(): string {
    return localStorage.getItem('userType') ?? '';
  }

  isArtistUserType(): boolean {
    const normalized = this.userType.trim().toUpperCase();
    return normalized === 'ARTISTA' || normalized === 'ARTIST';
  }

  isLabelUserType(): boolean {
    const normalized = this.userType.trim().toUpperCase();
    return normalized === 'LABEL';
  }

  signIn(credentials: { username: string; password: string }): Observable<ISignInResponse> {
    return this._http.post<ISignInResponse>(this.AUTH_API_URL + '/token/', credentials).pipe(
      switchMap((response: ISignInResponse) => {
        this.setSignInResponse(response);
        return this.getAccountProfile().pipe(
          map((profile: any) => {
            this.userType = (profile?.user_type ?? '').toString();
            const uuid = profile?.uuid;
            if (uuid) {
              this._amplitudeService.identifyUser(uuid);
              this._amplitudeService.setUserProperties({
                user_type: profile?.user_type,
                club_name: profile?.club?.club_name,
                club_id: profile?.club?.uuid,
                country_code: profile?.country_code,
                contract_signed: profile?.contract_signed,
                is_active: profile?.is_active,
                created_date: profile?.created,
              });
              this._amplitudeService.trackEvent('Login Succeeded', { userType: this.userType });
            }
            return response;
          }),
          catchError((error) => {
            return this.endSession().pipe(
              switchMap(() => throwError(() => error))
            );
          })
        );
      })
    );
  }

  signOut() {
    this._amplitudeService.trackEvent('Logout');
    this._amplitudeService.resetUser();
    this.endSession();
    this._navigationService.navigateToSignIn();
  }

  check(): Observable<boolean> {
    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return this.refreshTokenSignIn();
    }

    // Check if the user is logged in
    if (this.IsLoggedIn()) {
      return of(this.IsLoggedIn());
    }

    return of(!!this.accessToken || false);
  }

  private refreshTokenSignIn(): Observable<boolean> {
    if (!this.refreshToken) {
      return this.endSession();
    }
    return this._http.post<ISignInResponse>(this.AUTH_API_URL + '/token/refresh/', { refresh: this.refreshToken }).pipe(
      switchMap((response: ISignInResponse) => {
        this.setSignInResponse(response);
        return of(true);
      }),
      catchError(() => {
        return this.endSession();
      })
    );
  }

  private setSignInResponse(response: ISignInResponse) {
    // Store the access token in the local storage
    this.accessToken = response.access;
    this.refreshToken = response.refresh;

    // Set the logged in to true
    this.IsLoggedIn.set(true);
  }

  private endSession(): Observable<boolean> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userType');

    // Set the logged in to false
    this.IsLoggedIn.set(false);

    return of(false);
  }

  socialJwtPair(request: any) {
    return this._http.post(`${this.AUTH_API_URL}/social/jwt-pair/`, request)
  }

  getAccountProfile() {
    return this._http.get(`${this.ACCOUNT_API_URL}/profile/`);
  }
}
